import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BrainCircuit, Loader2, X, Maximize2, Minimize2, HardDrive, Database, Trash2, ExternalLink } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { format } from 'date-fns';
import ReactFlow, { 
  Node, 
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Panel,
  NodeProps,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { supabase } from "@/integrations/supabase/client";
import { useOnboardingConfig } from '@/components/onboarding/OnboardingSteps';
import { useNavigate } from 'react-router-dom';

interface GraphData {
  nodes: Node[];
  edges: Edge[];
}

interface TrainingDataItem {
  id: string;
  source_url: string;
  created_at: string;
}

interface PaginationState {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

interface NodeData {
  label: string;
  type?: string;
  properties?: Record<string, any>;
  isHighlighted?: boolean;
}

// Define color helper outside of components
const getNodeColor = (type: string | undefined, isHighlighted: boolean = false): string => {
  const baseColors: { [key: string]: { normal: string; highlighted: string } } = {
    organization: { normal: '#90cdf4', highlighted: '#3182ce' },
    person: { normal: '#fbd38d', highlighted: '#d69e2e' },
    event: { normal: '#9ae6b4', highlighted: '#38a169' },
    location: { normal: '#feb2b2', highlighted: '#e53e3e' },
    concept: { normal: '#e9d8fd', highlighted: '#805ad5' }
  };
  const defaultColor = { normal: '#cbd5e0', highlighted: '#718096' };
  const colors = type ? baseColors[type] || defaultColor : defaultColor;
  return isHighlighted ? colors.highlighted : colors.normal;
};

// Custom node component
const CustomNode = ({ data, selected }: NodeProps<NodeData>) => {
  const style = {
    backgroundColor: data.isHighlighted ? getNodeColor(data.type, true) : getNodeColor(data.type, false),
    borderColor: selected ? '#2563eb' : data.isHighlighted ? '#2563eb' : '#e5e7eb',
    borderWidth: selected || data.isHighlighted ? '2px' : '1px',
  };

  return (
    <div className="px-4 py-2 shadow-md rounded-md bg-white border-2" style={style}>
      <Handle type="target" position={Position.Top} className="w-2 h-2" />
      <div className="flex flex-col">
        <div className="font-bold text-sm">{data.label}</div>
        {data.type && (
          <div className="text-xs text-gray-500 capitalize">{data.type}</div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="w-2 h-2" />
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

export const GraphPage = ({ profile, session }: { profile: any | null, session: any | null }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRetraining, setIsRetraining] = useState(false);
  const [isLoadingTrainingData, setIsLoadingTrainingData] = useState(false);
  const [isDeletingTrainingData, setIsDeletingTrainingData] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{ success: boolean; message: string } | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showRetrainConfirmation, setShowRetrainConfirmation] = useState(false);
  const [showTrainingDataModal, setShowTrainingDataModal] = useState(false);
  const [trainingData, setTrainingData] = useState<TrainingDataItem[]>([]);
  const [knowledgebaseId, setKnowledgebaseId] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    total: 0,
    hasMore: false
  });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const graphRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { updateConfig } = useOnboardingConfig();
  const navigate = useNavigate();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      // Automatically trigger upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      if (!session?.access_token) {
        setUploadResult({
          success: false,
          message: 'Please log in to upload documents'
        });
        return;
      }

      setIsUploading(true);
      setUploadResult(null);

      try {
        // Get user ID from session
        const userId = session.user.id;
        
        // Upload to Supabase storage
        const fileExt = selectedFile.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${userId}/Knowledgebase/${fileName}`;
        
        await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token
        })
        // Upload file to Supabase storage
        const { error: uploadError } = await supabase.storage
          .from('nonprofit-cloud')
          .upload(filePath, selectedFile);
          
        if (uploadError) throw uploadError;
        
        // Get the public URL of the uploaded file
        const { data: { publicUrl } } = supabase.storage
          .from('nonprofit-cloud')
          .getPublicUrl(filePath);
        
        const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/knowledgebase/document`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            content_type: 'document',
            file_url: publicUrl
          })
        });

        const result = await response.json();
        
        setUploadResult({
          success: response.ok,
          message: result.message || (response.ok ? 'Document uploaded successfully!' : 'Failed to upload document')
        });

        if (response.ok) {
          // Refresh graph data after successful upload
          await fetchGraphData();
          updateConfig({ knowledge_base: true })
        }
      } catch (error) {
        console.error('Error uploading document:', error);
        setUploadResult({
          success: false,
          message: 'An error occurred while uploading the document'
        });
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setFile(null);
      }
    }
  };

  const handleClearGraph = async () => {
    if (!session?.access_token) return;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/knowledgebase/graph`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        setNodes([]);
        setEdges([]);
        setShowClearConfirmation(false);
      }
    } catch (error) {
      console.error('Error clearing graph:', error);
    }
  };

  const handleRetrainModel = async (showConfirmation = true) => {
    if (!session?.access_token) return;
    
    setIsRetraining(true);
    if (showConfirmation) {
      setShowRetrainConfirmation(false);
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/knowledgebase/retrain`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setUploadResult({
          success: true,
          message: 'Knowledge model retrained successfully'
        });
        
        // Refresh graph data after retraining
        await fetchGraphData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setUploadResult({
          success: false,
          message: errorData.message || 'Failed to retrain knowledge model'
        });
      }
    } catch (error) {
      console.error('Error retraining model:', error);
      setUploadResult({
        success: false,
        message: 'An error occurred while retraining the knowledge model'
      });
    } finally {
      setIsRetraining(false);
    }
  };

  const fetchGraphData = async () => {
    if (!session?.access_token) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/knowledgebase/graph`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      const result = await response.json();
      console.log('Graph API response:', result);
      
      if (response.ok && result.data) {
        // Handle both array and object response structures
        const graphData = Array.isArray(result.data) 
          ? (result.data.length > 0 ? result.data[0].graphed_content : null)
          : (result.data.graphed_content ? result.data.graphed_content : null);
        
        // Store the knowledgebase ID
        if (Array.isArray(result.data) && result.data.length > 0) {
          setKnowledgebaseId(result.data[0].id || "");
        } else if (result.data.id) {
          setKnowledgebaseId(result.data.id);
        }
        
        if (graphData && graphData.nodes && graphData.edges) {
          // Transform nodes to React Flow format with custom type
          const flowNodes = graphData.nodes.map((node: any) => ({
            id: node.id,
            type: 'custom',
            data: { 
              label: node.label,
              type: node.type,
              properties: node.properties
            },
            position: { x: 0, y: 0 }, // Initial position
            style: {
              background: getNodeColor(node.type),
              color: '#000',
              minWidth: 150,
            }
          }));

          // Transform edges to React Flow format
          const flowEdges = graphData.edges.map((edge: any) => ({
            id: `${edge.source}-${edge.target}`,
            source: edge.source,
            target: edge.target,
            label: edge.label,
            type: 'smoothstep',
            animated: true,
            style: { stroke: '#555' },
            labelStyle: { fill: '#555', fontSize: 12 }
          }));

          // Apply force-directed layout
          const positions = applyForceLayout(flowNodes, flowEdges);
          flowNodes.forEach((node, index) => {
            node.position = positions[index];
          });

          setNodes(flowNodes);
          setEdges(flowEdges);
        } else {
          console.log('No graph data found in the response');
        }
      }
    } catch (error) {
      console.error('Error fetching graph data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyForceLayout = (nodes: Node[], edges: Edge[]): { x: number; y: number }[] => {
    const positions: { x: number; y: number }[] = [];
    const spacing = 250; // Increased spacing
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    nodes.forEach((_, index) => {
      const angle = (index * 2 * Math.PI) / nodes.length;
      const radius = spacing * Math.sqrt(nodes.length);
      positions.push({
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      });
    });
    
    return positions;
  };

  const handleNodeClick = useCallback((event: React.MouseEvent, clickedNode: Node) => {
    setSelectedNode(clickedNode);
    
    // Find connected nodes and edges
    const connectedEdges = edges.filter(
      edge => edge.source === clickedNode.id || edge.target === clickedNode.id
    );
    
    const connectedNodeIds = new Set([
      ...connectedEdges.map(edge => edge.source),
      ...connectedEdges.map(edge => edge.target)
    ]);

    // Update nodes with highlighting
    setNodes(nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isHighlighted: connectedNodeIds.has(node.id)
      }
    })));

    // Update edges with highlighting
    setEdges(edges.map(edge => ({
      ...edge,
      style: {
        ...edge.style,
        stroke: connectedEdges.includes(edge) ? '#2563eb' : '#555',
        strokeWidth: connectedEdges.includes(edge) ? 2 : 1,
      },
      animated: connectedEdges.includes(edge),
    })));
  }, [nodes, edges, setNodes, setEdges]);

  const handlePaneClick = useCallback(() => {
    setSelectedNode(null);
    
    // Reset highlighting
    setNodes(nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isHighlighted: false
      }
    })));

    setEdges(edges.map(edge => ({
      ...edge,
      style: {
        ...edge.style,
        stroke: '#555',
        strokeWidth: 1,
      },
      animated: false,
    })));
  }, [nodes, edges, setNodes, setEdges]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      graphRef.current?.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  // Fetch training data with pagination
  const fetchTrainingData = async (page = 1) => {
    if (!session?.access_token || !knowledgebaseId) return;
    
    if (page === 1) {
      setShowTrainingDataModal(true);
    }
    
    setIsLoadingTrainingData(true);
    
    try {
      const limit = pagination.limit;
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/tools/knowledgebase/checkpoints/${knowledgebaseId}?page=${page}&limit=${limit}`, 
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        // Handle nested data structure
        const resultData = result.data?.data ? result.data.data : [];
        
        if (page === 1) {
          // Reset data for first page
          setTrainingData(resultData);
        } else {
          // Append data for subsequent pages
          setTrainingData(prev => [...prev, ...resultData]);
        }
        
        // Update pagination state with nested metadata
        setPagination({
          page,
          limit,
          total: result.data?.total || 0,
          hasMore: (result.data?.totalPages > page) || false
        });
      } else {
        console.error('Error fetching training data:', await response.text());
      }
    } catch (error) {
      console.error('Error fetching training data:', error);
    } finally {
      setIsLoadingTrainingData(false);
    }
  };
  
  // Load more training data
  const loadMoreTrainingData = () => {
    if (pagination.hasMore && !isLoadingTrainingData) {
      fetchTrainingData(pagination.page + 1);
    }
  };
  
  // Delete training data item
  const handleDeleteTrainingData = async (id: string) => {
    if (!session?.access_token) return;
    
    setIsDeletingTrainingData(id);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/tools/knowledgebase/checkpoint/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });
      
      if (response.ok) {
        // Remove the deleted item from the list
        setTrainingData(trainingData.filter(item => item.id !== id));
        
        setUploadResult({
          success: true,
          message: 'Training data removed successfully'
        });
        
        // Trigger model retrain after successful deletion
        await handleRetrainModel(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setUploadResult({
          success: false,
          message: errorData.message || 'Failed to remove training data'
        });
      }
    } catch (error) {
      console.error('Error deleting training data:', error);
      setUploadResult({
        success: false,
        message: 'An error occurred while removing training data'
      });
    } finally {
      setIsDeletingTrainingData(null);
    }
  };

  useEffect(() => {
    if (!session?.access_token) {
      return;
    }
    fetchGraphData();
  }, [session?.access_token]);

  if (!session?.access_token) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" ref={graphRef}>
      <Card className="space-y-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BrainCircuit className="h-6 w-6" />
              Knowledge Graph
            </CardTitle>
            <CardDescription>
            <p className="mt-2 text-muted-foreground">Upload your relevant documents for the AI to build a knowledge graph about you. 
              Supported formats are PDF, DOC, and TXT.
              </p>
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center">
            <div className="flex gap-4 items-center">
              <input
                type="file"
                onChange={handleFileChange}
                className="hidden"
                ref={fileInputRef}
                accept=".pdf,.doc,.docx,.txt"
              />
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload File to Knowledge Base'
                )}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowClearConfirmation(true)}
                className="flex items-center gap-2"
              >
                Clear Knowledge Graph
              </Button>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => fetchTrainingData()}
                className="flex items-center gap-2"
              >
                <Database className="h-4 w-4 mr-2" />
                Training Data
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setShowRetrainConfirmation(true)}
                className="flex items-center gap-2"
                disabled={isRetraining}
              >
                {isRetraining ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Retraining...
                  </>
                ) : (
                  <>
                    <BrainCircuit className="h-4 w-4 mr-2" />
                    Retrain Model
                  </>
                )}
              </Button>
            </div>
          </div>
          {uploadResult && (
            <div className={`mt-4 p-4 rounded-md ${uploadResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {uploadResult.message}
              <button
                onClick={() => setUploadResult(null)}
                className="float-right hover:opacity-75"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {showClearConfirmation && (
            <div className="mt-4 p-4 bg-yellow-50 text-yellow-700 rounded-md">
              <p>This will clear all your contextual learnings. Are you sure?</p>
              <div className="mt-2 flex gap-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleClearGraph}
                >
                  Yes, Clear
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowClearConfirmation(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          {showRetrainConfirmation && (
            <div className="mt-4 p-4 bg-blue-50 text-blue-700 rounded-md">
              <p>This will retrain the knowledge model with your current data. This process may take a few minutes.</p>
              <div className="mt-2 flex gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleRetrainModel}
                >
                  Yes, Retrain
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRetrainConfirmation(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex-1 relative">
        <div className={`h-[600px] ${isFullScreen ? 'fixed inset-0 z-50 h-screen' : ''}`}>
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50/50">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              onNodeClick={handleNodeClick}
              onPaneClick={handlePaneClick}
              className="bg-white"
              fitView
              minZoom={0.1}
              maxZoom={1.5}
            >
              <Panel position="top-right">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleFullScreen}
                >
                  {isFullScreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </Button>
              </Panel>
              <Controls />
              <Background color="#aaa" gap={16} />
              
              {/* Legend */}
              <Panel position="bottom-left" className="bg-white p-4 rounded-lg shadow-lg">
                <div className="space-y-2">
                  <h4 className="font-bold text-sm mb-2">Node Types</h4>
                  {Object.entries({
                    Organization: 'organization',
                    Person: 'person',
                    Event: 'event',
                    Location: 'location',
                    Concept: 'concept'
                  }).map(([label, type]) => (
                    <div key={type} className="flex items-center space-x-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: getNodeColor(type) }}
                      />
                      <span className="text-sm">{label}</span>
                    </div>
                  ))}
                </div>
              </Panel>

              {/* Node details panel */}
              {selectedNode && (
                <Panel position="top-right" className="bg-white p-4 rounded-lg shadow-lg w-64 mt-12">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold">{selectedNode.data.label}</h3>
                    <button 
                      onClick={() => setSelectedNode(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium">Type: </span>
                      <span className="text-sm capitalize">{selectedNode.data.type}</span>
                    </div>
                    {selectedNode.data.properties && Object.entries(selectedNode.data.properties).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-sm font-medium capitalize">{key}: </span>
                        <span className="text-sm">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              )}
            </ReactFlow>
          )}
        </div>
      </div>
      
      {/* Training Data Modal */}
      {showTrainingDataModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Training Data</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowTrainingDataModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {isLoadingTrainingData && trainingData.length === 0 ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : trainingData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No training data available</p>
              </div>
            ) : (
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="overflow-y-auto overflow-x-auto flex-1 pr-1" style={{ scrollbarWidth: 'thin' }}>
                  <table className="w-full border-collapse">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b">
                        <th className="text-left py-2 px-4">Training Data (URL)</th>
                        <th className="text-left py-2 px-4">Training Date</th>
                        <th className="text-center py-2 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(trainingData) && trainingData.map((item, index) => (
                        <tr key={item.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <ExternalLink className="h-4 w-4 text-gray-400" />
                              <a 
                                href={item.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="truncate max-w-md text-blue-600 hover:text-blue-800 hover:underline" 
                                title={item.source_url}
                              >
                                {item.source_url.split('/').pop() || item.source_url}
                              </a>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            {format(new Date(item.created_at), 'MMM d, yyyy h:mm a')}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteTrainingData(item.id)}
                              disabled={isDeletingTrainingData === item.id}
                            >
                              {isDeletingTrainingData === item.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Remove
                                </>
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Infinite scroll trigger */}
                  {pagination.hasMore && !isLoadingTrainingData && (
                    <InfiniteScrollTrigger loadMore={loadMoreTrainingData} />
                  )}
                  
                  {isLoadingTrainingData && (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                    </div>
                  )}
                </div>
                
                <div className="text-center text-sm text-gray-500 mt-2 py-2 border-t">
                  Showing {trainingData.length} of {pagination.total} items
                </div>
              </div>
            )}
            
            <div className="flex justify-end mt-4 pt-2">
              <Button variant="outline" onClick={() => setShowTrainingDataModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Infinite scroll trigger component
const InfiniteScrollTrigger = ({ loadMore }: { loadMore: () => void }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView) {
      loadMore();
    }
  }, [inView, loadMore]);

  return <div ref={ref} className="h-1" />;
};

export default GraphPage;
